import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors } from "@angular/forms";
import { TranslocoDirective } from "@jsverse/transloco";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { Subscription } from "rxjs";
import { JsonEditorComponent } from "../../codemirror-wrapper/codemirror-wrapper";

/* 
  Usage example:
  {
    key: "jsonInput",
    type: "json",
    props: {
      required: true,
      label: {
        en: "JSON Input",
        fi: "JSON-syöte",
        sv: "JSON-inmatning",
        no: "JSON-inngang",
      }, // Optional
      description: {
        en: "Define custom fields for the process in JSON format",
        fi: "Määrittele prosessille JSON-muodossa",
        sv: "Definiera fält för processen i JSON-format",
        no: "Definer fält for prosessen i JSON-format",
      }, // Optional
    },
  },


  JSON Format:
  {
    "key": "jsonInput",
    "type": "json",
    "props": {
      "required": true,
      "label": {
        "en": "JSON Input",
        "fi": "JSON-syöte",
        "sv": "JSON-inmatning",
        "no": "JSON-inngang"
      }, // Optional
      "description": {
        "en": "Define custom fields for the process in JSON format",
        "fi": "Määrittele prosessille JSON-muodossa",
        "sv": "Definiera fält för processen i JSON-format",
        "no": "Definer fält for prosessen i JSON-format"
      } // Optional
    }
  },
*/
@Component({
  selector: "app-json-input",
  templateUrl: "./json.type.html",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, JsonEditorComponent, TranslocoDirective],
})
export class JsonInputComponent extends FieldType<FieldTypeConfig> implements OnInit, OnDestroy {
  jsonControl = new FormControl<string>("");

  private subscription?: Subscription;
  jsonError: string | null = null;

  ngOnInit(): void {
    // The editor is its own control, so it doesn't inherit Formly's `required` validator — apply it from the
    // field's `required` prop here. Empty is allowed (and valid) when the field isn't required.
    this.jsonControl.setValidators((control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value ?? "";
      if (value.trim() === "") {
        return this.props.required ? { required: true } : null;
      }
      try {
        JSON.parse(value);
        return null;
      } catch {
        return { jsonInvalid: true };
      }
    });

    const initialValue = this.normalizeJson(this.formControl.value || "{}", 2);
    this.jsonControl.setValue(initialValue, { emitEvent: false });

    this.subscription = this.jsonControl.valueChanges.subscribe((raw) => {
      const value = raw ?? "";

      if (value.trim() === "") {
        // Mirror the required rule onto the Formly control so submit is blocked only when the field is required.
        this.jsonError = null;
        this.formControl.setValue("");
        this.formControl.setErrors(this.props.required ? { required: true } : null);
      } else {
        try {
          const normalized = this.normalizeJson(value);
          this.formControl.setValue(normalized);
          this.jsonError = null;
          this.formControl.setErrors(null);
        } catch (e: unknown) {
          this.jsonError = e instanceof Error ? e.message : "Invalid JSON";
          this.formControl.setErrors({ jsonInvalid: true });
        }
      }

      this.formControl.markAsDirty();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onBlur(): void {
    this.formControl.markAsTouched();

    if (!this.jsonControl.errors) {
      const formatted = this.normalizeJson(this.jsonControl.value || "{}", 2);
      this.formControl.setValue(this.normalizeJson(formatted));
    }
  }

  isFieldInvalid(): boolean {
    const c = this.jsonControl;
    return !!(c.invalid && (c.dirty || c.touched));
  }

  private normalizeJson(value: string, pretty?: number): string {
    const parsed = JSON.parse(value || "{}");
    return JSON.stringify(parsed, null, pretty ?? undefined);
  }
}
